import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ProductGallery } from "../components/ProductGallery";
import { SizeGuide } from "../components/SizeGuide";
import { toast } from "sonner";

const ease = [0.22, 1, 0.36, 1];

const SizePicker = ({ sizes, inv, tracked, size, setSize, category }) => {
  const selectedLeft = size && tracked ? (inv[size] || 0) : null;
  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <p className="label">Select Size</p>
          <SizeGuide category={category} sizes={sizes} />
        </div>
        {selectedLeft != null && selectedLeft > 0 && selectedLeft <= 10 && (
          <span className="label text-[#2B6BFF]" data-testid="stock-note">Only {selectedLeft} left</span>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        {sizes.map((s) => {
          const soldOut = tracked && (inv[s] || 0) <= 0;
          const selected = size === s;
          return (
            <button
              key={s}
              data-testid={`size-${s}`}
              disabled={soldOut}
              onClick={() => !soldOut && setSize(s)}
              title={soldOut ? "Sold out" : undefined}
              className={`relative w-14 h-14 label border transition-colors duration-300 ${
                soldOut
                  ? "border-[#D4D7DD] text-[#B8BCC4] cursor-not-allowed line-through"
                  : selected
                  ? "bg-[#0B0E14] text-[#EDEEF1] border-[#0B0E14]"
                  : "border-[#0B0E14] hover:bg-[#0B0E14] hover:text-[#EDEEF1]"
              }`}
            >
              {s}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setSize(null); }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-40 text-center">
      <p className="font-serif-display text-4xl">Piece not found</p>
      <Link to="/shop" className="label link-underline mt-4 inline-block">Back to collection</Link>
    </div>
  );
  if (!product) return <div className="pt-40 h-screen" />;

  const gallery = product.images && product.images.length ? product.images : [product.image];
  const inv = product.inventory || {};
  const tracked = Object.keys(inv).length > 0;
  const allSoldOut = tracked && product.sizes.every((s) => (inv[s] || 0) <= 0);

  const onAdd = () => {
    if (!size) { toast.error("Please select a size"); return; }
    if (tracked && (inv[size] || 0) <= 0) { toast.error("That size is sold out"); return; }
    add(product, size);
  };

  return (
    <div data-testid="product-page" className="pt-16 md:pt-20">
      <div className="grid grid-cols-1 md:grid-cols-2">
        <ProductGallery key={product.slug} name={product.name} images={gallery} />

        <div className="px-6 sm:px-10 md:px-16 py-14 md:py-24 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.1 }}
          >
            <button onClick={() => navigate(-1)} className="label text-[#8A8F99] flex items-center gap-2 mb-10 hover:text-[#0B0E14] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <p className="label text-[#8A8F99] mb-4">{product.category}</p>
            <h1 className="font-serif-display text-5xl md:text-6xl tracking-tight leading-none">{product.name}</h1>
            <p className="text-2xl mt-6 font-light">{fmtPrice(product.price)}</p>
            <p className="text-[#3A4150] leading-relaxed mt-8 max-w-md text-lg">{product.description}</p>

            <SizePicker sizes={product.sizes} inv={inv} tracked={tracked} size={size} setSize={setSize} category={product.category} />

            <button
              data-testid="add-to-cart"
              onClick={onAdd}
              disabled={allSoldOut}
              className="mt-12 w-full bg-[#0B0E14] text-[#EDEEF1] h-16 label flex items-center justify-center gap-2 hover:bg-[#1B2233] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" /> {allSoldOut ? "Sold Out" : `Add to Bag — ${fmtPrice(product.price)}`}
            </button>
            <p className="label text-[#8A8F99] mt-6">$5.99 flat-rate US shipping · 30-day returns</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
